import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import axios from 'axios';

/*
 * THESIS: Make choosing a branch feel like arriving somewhere, not filling a dashboard grid.
 * OWN-WORLD: Deep ink, warm paper, Winners red, and one human follow-up moment.
 * STORY: Visitors understand this is their church follow-up home, choose a branch, then act.
 * FIRST VIEWPORT: Branch actions stay left and legible; the conversation owns the right half.
 * FORM: Split editorial welcome page, as a focused refinement of the existing branch workflow.
 */
const accent = '#C9272C';
const ink = '#172033';
const muted = '#667085';
const heroImages = [
  { src: '/congregation-praise.webp', alt: 'Winners Chapel congregation praising together' },
  { src: '/hands-raised.webp', alt: 'Worshippers raising their hands in praise' }
];

const WelcomePage = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const rotation = window.setInterval(() => {
      setHeroImageIndex((currentIndex) => (currentIndex + 1) % heroImages.length);
    }, 6000);

    return () => window.clearInterval(rotation);
  }, []);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        setError('');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await axios.get(`${apiUrl}/api/organizations`, {
          timeout: 10000,
          headers: { 'Content-Type': 'application/json' }
        });
        setOrganizations((response.data.organizations || []).filter((org) => org.is_active));
      } catch (err) {
        console.error('Error fetching organizations:', err);
        if (err.response?.status === 404) {
          setError('The church branch list is unavailable. Please check the API configuration.');
        } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
          setError('We cannot connect to the follow-up server right now.');
        } else {
          setError('We could not load the church branches. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#F6F3EF' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={44} sx={{ color: accent }} />
          <Typography sx={{ color: muted }}>Preparing your church branches…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F6F3EF', color: ink }}>
      <Container maxWidth="xl" disableGutters>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(460px, 0.9fr) minmax(440px, 1.1fr)' }
          }}
        >
          <Box sx={{ px: { xs: 3, sm: 6, lg: 10 }, py: { xs: 5, lg: 8 }, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: 620 }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 5 }}>
                <Box component="img" src="/assets/church-logo.png" alt="Winners Chapel International logo" sx={{ width: 42, height: 48, objectFit: 'contain' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: accent }}>
                  Winners Chapel Canada
                </Typography>
              </Stack>

              <Typography component="h1" sx={{ maxWidth: 570, fontSize: { xs: '2.8rem', sm: '4.4rem' }, lineHeight: 0.98, letterSpacing: '-0.055em', fontWeight: 800, mb: 3 }}>
                Stay connected to your church family.
              </Typography>
              <Typography sx={{ maxWidth: 500, color: muted, fontSize: { xs: '1.05rem', sm: '1.2rem' }, lineHeight: 1.65, mb: 6 }}>
                Choose your branch to sign in or send a follow-up request to the right church team.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

              <Box component="section" aria-labelledby="branch-heading">
                <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2.5 }}>
                  <Typography id="branch-heading" component="h2" sx={{ fontSize: '0.85rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    Choose your branch
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(23, 32, 51, 0.14)' }} />
                </Stack>

                {organizations.length === 0 ? (
                  <Alert severity="info">No active church branches are available yet.</Alert>
                ) : (
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                      <Autocomplete
                        sx={{ flex: 1 }}
                        options={organizations}
                        value={selectedOrganization}
                        onChange={(_, organization) => setSelectedOrganization(organization)}
                        getOptionLabel={(organization) => organization.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => <TextField {...params} label="Find your church branch" placeholder="Search by city or church name" />}
                      />
                      <Button
                        variant="contained"
                        disabled={!selectedOrganization}
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/${selectedOrganization.slug}/login`)}
                        sx={{ bgcolor: accent, px: 2.5, borderRadius: 2, fontWeight: 800, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#A91F24' } }}
                      >
                        Sign in
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ position: 'relative', minHeight: { xs: 360, lg: '100vh' }, order: { xs: -1, lg: 0 }, overflow: 'hidden' }}>
            <Box component="img" src={heroImages[heroImageIndex].src} alt={heroImages[heroImageIndex].alt} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 500ms ease' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(16,23,40,0.08) 0%, rgba(16,23,40,0.66) 100%)' }} />
            <Box sx={{ position: 'absolute', left: { xs: 3, sm: 5, lg: 7 }, right: { xs: 3, sm: 5, lg: 7 }, bottom: { xs: 3, lg: 7 }, color: '#FFF' }}>
              <Typography sx={{ maxWidth: 420, fontSize: { xs: '1.7rem', lg: '2.3rem' }, lineHeight: 1.08, letterSpacing: '-0.03em', fontWeight: 700 }}>
                Follow-up that keeps people connected.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default WelcomePage;
